import type { ParentComponent, VoidComponent } from "solid-js";
import DetailModal from "../../../../../components/modal/DetailModal";
import { useTranslation } from "../../../../../state/i18n";
import type { IconName } from "../../../../../components/icon/Icon";
import { Icon } from "../../../../../components/icon/Icon";
import StatusButton from "../../../../../components/button/StatusButton";
import { noop } from "../../../../../utils/noop";
import { TextButton } from "../../../../../components/button/Button";
import { useCurrentModal, useModalManager } from "../../../../../components/modal/ModalManager";
import ConfirmCancellationModal from "./ConfirmCancellationModal";

type CancelSubscriptionModalProps = {
  subscriptionId: string;
  cycleEnd?: number;
};

const CancelSubscriptionModal: VoidComponent<CancelSubscriptionModalProps> = (props) => {
  const { t } = useTranslation();
  const { replace } = useModalManager();
  const { close } = useCurrentModal();

  const openMeeting = () =>
    window.open(
      t(($) => $.CANCEL_MODAL_GET_IN_TOUCH_CTA_LINK),
      "_blank",
      "noopener",
    );

  return (
    <DetailModal
      title={t(($) => $.CANCEL_MODAL_TITLE)}
      description={t(($) => $.CANCEL_MODAL_DESCRIPTION)}
      content={() => (
        <div class="flex flex-col gap-4">
          <CancelAlternative
            class="border-status-subtle-upgrade bg-surface-status-subtle-upgrade"
            iconClass="icon-status-upgrade"
            icon="sparkles"
            title={t(($) => $.CANCEL_MODAL_SWITCH_TITLE)}
            description={t(($) => $.CANCEL_MODAL_SWITCH_DESCRIPTION)}
          >
            <StatusButton
              variant="primary"
              label={t(($) => $.CANCEL_MODAL_SWITCH_CTA_TITLE)}
              status="upgrade"
              // TODO: Link to result of DEV-8569
              onClick={noop}
            />
          </CancelAlternative>

          <CancelAlternative
            class="border-subtle bg-surface-default"
            iconClass="border border-status-subtle-informative bg-surface-status-subtle-informative icon-button-icon-tertiary-active rounded-xs"
            icon="chatAlt"
            title={t(($) => $.CANCEL_MODAL_GET_IN_TOUCH_TITLE)}
            description={t(($) => $.CANCEL_MODAL_GET_IN_TOUCH_DESCRIPTION)}
          >
            <TextButton
              variant="secondary"
              text={t(($) => $.CANCEL_MODAL_GET_IN_TOUCH_CTA_TITLE)}
              size="medium"
              onClick={openMeeting}
            />
          </CancelAlternative>
        </div>
      )}
      fullHeight
      footer={
        <div class="flex w-full justify-end gap-2">
          <TextButton
            text={t(($) => $.CANCEL_MODAL_KEEP_SUBSCRIPTION_TITLE)}
            variant="secondary"
            onClick={() => close()}
          />
          <TextButton
            text={t(($) => $.CANCEL_MODAL_CONTINUE_CANCELLATION_TITLE)}
            variant="primary"
            onClick={() =>
              replace({
                content: () => (
                  <ConfirmCancellationModal subscriptionId={props.subscriptionId} cycleEnd={props.cycleEnd} />
                ),
              })
            }
          />
        </div>
      }
    />
  );
};

type CancelAlternativeProps = {
  icon: IconName;
  title: string;
  description: string;
  class: string;
  iconClass: string;
};

const CancelAlternative: ParentComponent<CancelAlternativeProps> = (props) => (
  <div class={`flex gap-3 rounded border p-4 ${props.class}`}>
    <div class={`flex size-9 shrink-0 items-center justify-center ${props.iconClass}`}>
      <Icon name={props.icon} size="xl" type="outline" />
    </div>
    <div class="flex flex-col items-start gap-4">
      <div class="flex flex-col gap-1">
        <h2 class="text-medium-semibold text-primary">{props.title}</h2>
        <p class="text-medium-regular text-primary">{props.description}</p>
      </div>
      {props.children}
    </div>
  </div>
);

export default CancelSubscriptionModal;
